import React, { useLayoutEffect, useRef, useState } from 'react';
import { Paper, FormControlLabel, Switch, Typography } from '@mui/material';
import { tokens } from '../../../theme';

/**
 * Floating menu for an image in the manuscript, opened by clicking the image.
 * Built the same way as TableContextMenu.
 *
 * One setting: whether the image may continue onto the next page rather than
 * being cut off at the bottom of this one. An image that shares its line with
 * words or another image (`shared`) cannot — the page split cuts such a
 * paragraph on its words — and for it the switch is shown disabled with the
 * reason, rather than hidden. An image with text wrapped round it can.
 *
 * Switched on, a line under it says whether the image actually runs on
 * (`continues`). Mid-page there is nothing to continue, and without the line
 * the switch looked as if it did nothing.
 */
export default function ImageContextMenu({ position, bleed, continues, shared, onToggleBleed }) {
  // Opened at the click, then moved left as far as it takes to keep the whole
  // menu on screen with a gutter either side — on a phone the click is often
  // too near the right edge for it to fit there.
  const ref = useRef(null);
  const [left, setLeft] = useState(null);
  useLayoutEffect(() => {
    if (!position || !ref.current) return;
    const most = window.innerWidth - tokens.space.lg - ref.current.offsetWidth;
    setLeft(Math.max(tokens.space.lg, Math.min(position.x, most)));
  }, [position, bleed, continues, shared]);

  if (!position) return null;
  const reason = shared ? 'Only available for an image on a line of its own.' : null;
  // Turning it off is always allowed: an image that was running and then
  // gained a caption must not be stuck showing "on".
  const locked = !!reason && !bleed;
  const status = bleed && !reason
    ? (continues
      ? 'Continues onto the next page.'
      : 'This image fits on its page. Drag it to the bottom of a page to continue it onto the next.')
    : null;
  const note = reason || status;

  return (
    <Paper
      ref={ref}
      elevation={8}
      sx={{
        position: 'fixed',
        left: left ?? position.x,
        top: position.y,
        width: 'max-content',
        maxWidth: `calc(100vw - ${2 * tokens.space.lg}px)`,
        zIndex: 9999,
        px: `${tokens.space.md}px`,
        py: `${tokens.space.sm}px`,
        bgcolor: tokens.color.card,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: `${tokens.radius.panel}px`,
      }}
    >
      <FormControlLabel
        sx={{ m: 0, gap: `${tokens.space.sm}px` }}
        control={
          <Switch
            size="small"
            checked={!!bleed}
            disabled={locked}
            onChange={(e) => onToggleBleed(e.target.checked)}
            inputProps={{ 'aria-label': 'Continue onto next page' }}
          />
        }
        label={
          <Typography sx={{ fontSize: tokens.text.label.size, fontWeight: tokens.text.label.weight, color: tokens.color.ink }}>
            Continue onto next page
          </Typography>
        }
      />
      {note && (
        <Typography sx={{ mt: `${tokens.space.xs}px`, maxWidth: '34ch', fontSize: tokens.text.label.size, color: tokens.color.muted }}>
          {note}
        </Typography>
      )}
    </Paper>
  );
}
